import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { auth, db } from '../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        // Check if user has admin/manager role
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // Only allow admin and manager roles to access admin panel
            if (userData.role === 'admin' || userData.role === 'manager') {
              setCurrentUser(user);
            } else {
              console.warn('⚠️ User does not have admin/manager role:', userData.role);
              await signOut(auth);
              setCurrentUser(null);
            }
          } else {
            // User document doesn't exist in Firestore
            console.warn('⚠️ User document not found in Firestore');
            await signOut(auth);
            setCurrentUser(null);
          }
        } catch (error) {
          console.error('Error checking user role:', error);
          await signOut(auth);
          setCurrentUser(null);
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔐 Attempting admin login for:', email);

      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Firebase Auth successful, UID:', userCredential.user.uid);

      // Check if user has admin/manager role
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      console.log('📊 Checking users collection...');

      if (!userDoc.exists()) {
        console.error('❌ User document not found in users collection');
        await signOut(auth);
        return { success: false, error: 'User account not found in system. Contact admin.' };
      }

      const userData = userDoc.data();
      console.log('👤 User data:', { role: userData.role, isActive: userData.isActive });

      // Only allow admin and manager roles
      if (userData.role !== 'admin' && userData.role !== 'manager') {
        console.error(`❌ Invalid role: "${userData.role}". Expected: "admin" or "manager"`);
        await signOut(auth);
        return {
          success: false,
          error: `Access denied. Admin panel is for administrators only. Your role: ${userData.role}`
        };
      }

      // Check if user is active
      const isActive = userData.isActive !== undefined ? userData.isActive : true;
      if (!isActive) {
        console.error('❌ User account is deactivated');
        await signOut(auth);
        return { success: false, error: 'Your account has been deactivated. Contact admin.' };
      }

      console.log('✅ All checks passed! Admin login successful');
      return { success: true, user: userCredential.user };
    } catch (error) {
      console.error('❌ Login error:', error);

      let errorMessage = 'Login failed. Please try again.';

      if (error.code === 'auth/invalid-credential' || error.code === 'auth/wrong-password') {
        errorMessage = 'Invalid email or password';
      } else if (error.code === 'auth/user-not-found') {
        errorMessage = 'User not found';
      } else if (error.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format';
      }

      return { success: false, error: errorMessage };
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      return { success: true };
    } catch (error) {
      console.error('Logout error:', error);
      return { success: false, error: error.message };
    }
  };

  const value = {
    currentUser,
    login,
    logout,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
